import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Empty, Flex, Skeleton, Typography } from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import TransactionsList from './TransactionsList';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { Transaction, TransactionDetails } from 'widgets/transactions/model/types';
import { debounce } from 'lodash';
import { getErrorDefaultMessage } from 'shared/libs/helpers';
import Result from 'shared/ui/Result';
import TransactionDetailsModal from './TransactionDetails';
import Filters from '../components/Filters';
import { groupTransactions } from '../model/utils';
import { MonthSpending } from '../components/MonthSpending';
import { MONTH_CONFIG } from '../model/constants';
import {
  useGetFiltersQuery,
  useLazyGetTransactionByIdQuery,
  useLazyGetTransactionsQuery
} from '../api';
import { GetTransactionsParams } from '../api/dto';
import Search from '../components/Search';

type TransactionsViewProps = {
  accountId: number;
  showTitle?: boolean;
};

const TransactionsView: React.FC<TransactionsViewProps> = ({ accountId, showTitle = true }) => {
  const { formatMessage: t } = useIntl();
  const [fetchTransactions, { data, error, isError, isLoading, isFetching, isSuccess }] =
    useLazyGetTransactionsQuery();
  const [query, setQuery] = useState('');
  const { data: filtersData } = useGetFiltersQuery({ accountId });
  const config = useRef<GetTransactionsParams>(MONTH_CONFIG);
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true);
  const [activeTransaction, setActiveTransaction] = useState<Transaction>();
  const [activeTransactionDetails, setActiveTransactionDetails] = useState<TransactionDetails>();
  const { showError } = useMessage();
  const [getTransactionById] = useLazyGetTransactionByIdQuery();
  const [transactionLoading, setTransactionLoading] = useState(false);
  // Use current month start for MonthSpending to show spending only for current month
  const currentMonthStart = moment().startOf('month').format('DD-MM-YYYY');

  const onFetchTransactions = (options?: { nextPage: boolean }) => {
    fetchTransactions({
      accountId: accountId,
      params: config.current,
      merge: config.current.page !== 0
    })
      .unwrap()
      .then(({ response }) => {
        if (config.current.page >= Number(response.totalPages) - 1) {
          setCanLoadMore(false);
        } else {
          setCanLoadMore(true);
        }

        config.current = {
          ...config.current,
          page: config.current.page + (options?.nextPage === false ? 0 : 1)
        };
      });
  };

  useEffect(() => {
    onFetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedFetchTransactions = useCallback(
    debounce((params) => onFetchTransactions(params), 500),
    []
  );

  const onChangeQuery = (input: string) => {
    setQuery(input);
    config.current = {
      ...config.current,
      page: 0,
      query: input
    };

    debouncedFetchTransactions({ nextPage: false });
  };

  const renderFilters = () => {
    if (filtersData?.response)
      return (
        <Filters
          filters={filtersData?.response.filters}
          onSelect={(type) => {
            const newType = type === 'All' || !type ? undefined : type;
            config.current = {
              ...config.current,
              filter: newType,
              page: 0
            };

            onFetchTransactions();
          }}
        />
      );
  };

  const onClickTransaction = (transaction: Transaction) => {
    setActiveTransaction(transaction);
    setTransactionLoading(true);
    getTransactionById({
      accountId,
      transactionId: transaction.id
    })
      .unwrap()
      .then((response) => {
        setActiveTransactionDetails(response.response);
      })
      .catch(() => {
        showError(t({ id: 'common.message.error' }));
      })
      .finally(() => {
        setTransactionLoading(false);
      });
  };

  const renderList = () => {
    if (isError) {
      return (
        <Flex vertical align='center' gap={16} style={{ width: '100%', marginTop: 32 }}>
          <Result
            type='error'
            title={t({ id: 'drawer.transactions.wrong' })}
            subtitle={getErrorDefaultMessage(error)}
          />
          <Button
            onClick={() => onFetchTransactions()}
            type='primary'
            style={{ backgroundColor: 'black' }}
          >
            {t({ id: 'drawer.transactions.refresh' })}
          </Button>
        </Flex>
      );
    }

    if (isLoading) {
      return (
        <Flex gap={16} vertical style={{ width: '100%' }}>
          {showTitle && (
            <Typography.Title level={3} style={{ marginBottom: 16 }}>
              {t({ id: 'transactions.title' })}
            </Typography.Title>
          )}
          <Search value={query} onChange={onChangeQuery} />
          <Flex>{renderFilters()}</Flex>
          <MonthSpending accountId={accountId} start={currentMonthStart} />
          <Skeleton active />
        </Flex>
      );
    }

    if (isSuccess) {
      if (!data) return null;
      const transactionsList = data.response.transactions;
      const groupedData = groupTransactions(transactionsList);

      return (
        <Flex vertical style={{ width: '100%' }}>
          {showTitle && (
            <Typography.Title level={3} style={{ marginBottom: 16 }}>
              {t({ id: 'transactions.title' })}
            </Typography.Title>
          )}
          <Flex gap={16} vertical>
            <Search value={query} onChange={onChangeQuery} />
            <Flex>{renderFilters()}</Flex>
            <MonthSpending accountId={accountId} start={currentMonthStart} />
            <Skeleton loading={isLoading || isFetching}>
              {transactionsList.length ? (
                <TransactionsList
                  transactions={groupedData}
                  onClickTransaction={onClickTransaction}
                />
              ) : (
                <Flex vertical align='center' style={{ width: '100%' }}>
                  <Empty description={t({ id: 'drawer.transactions.notFound' })} />
                </Flex>
              )}
            </Skeleton>
            {canLoadMore && (
              <div style={{ paddingInline: 16 }}>
                <Button
                  style={{ marginBottom: 32, backgroundColor: 'black', color: 'white' }}
                  disabled={isFetching}
                  onClick={() => onFetchTransactions()}
                >
                  {t({ id: 'buttons.loadMore' })}
                </Button>
              </div>
            )}
          </Flex>
        </Flex>
      );
    }

    return null;
  };

  return (
    <>
      <Flex style={{ marginBottom: 32 }} justify='space-between'>
        {renderList()}
      </Flex>
      <TransactionDetailsModal
        transaction={activeTransaction}
        transactionDetails={activeTransactionDetails}
        onClose={() => setActiveTransaction(undefined)}
        loading={transactionLoading}
      />
    </>
  );
};

export default TransactionsView;

